import {
	faShareNodes,
	faStar,
	faSensorAlert,
	faPencil,
	faCalendar,
	faSparkles,
	faDownToBracket,
} from '@fortawesome/pro-regular-svg-icons';
import { faSparkles as faSparkleSolid } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	AlertVariant,
	Button,
	Checkbox,
	Dropdown,
	DropdownItem,
	DropdownToggle,
	Flex,
	FlexItem,
} from '@patternfly/react-core';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import ShareEntityModal from './ShareEntityModal';
import { useApplication, useIsGranted } from '../../components/user/ApplicationProvider';
import { Permission } from '../../enums/permission.enum';
import CsvDownloader from 'react-csv-downloader';
import { MultipartResponse, rebuildCsvStringArray } from '../multipart-response.helper';
import { DataframeDataRetrievalRequest, DataframeDataRetrievalResponse } from '../../api/types';
import { Columns, Datas, IColumn } from 'react-csv-downloader/dist/esm/lib/csv';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { dateToYYYYMMDDHHMMSS } from '../../utilities';
import { Favorites, TFavorites } from '../../api/favorites/Favorites';
import { DataBuilderTypes } from '../../enums/data-builder-types';
import AlertDefinitionModal from './AlertDefinitionModal';
import { DraggableMenuItemData } from '../../types/databuilder/databuilder';
import { useNavigate, useLocation } from 'react-router-dom';
import { TKeyMeasure } from '../../api/analytics/KeyMeasure';
import { faPersonChalkboard } from '@fortawesome/pro-light-svg-icons';
import * as XLSX from 'xlsx';
import { useMount } from 'react-use';
import { ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { Dataframe } from '../../api/dataframes/Dataframes';
import { buildExportRequest } from '../export-request.helper';
import Loader from '../../components/util/Loader';

type Props = {
	entityType: string;
	entityName: string;
	canShare: boolean;
	canEdit?: boolean;
	exportData?: MultipartResponse<DataframeDataRetrievalResponse>;
	limitReached?: boolean;
	favorite?: TFavorites;
	setFavorite?: () => void;
	droppedFacts?: DraggableMenuItemData[];
	isView: boolean;
	exportPresentation?: () => void;
	present?: () => void;
	openInsights?: (isOpen: boolean) => void;
	setisInsightsOpen?: Dispatch<SetStateAction<boolean>>;
	isInsightsOpen?: boolean;
	isColTotalsChecked?: boolean;
	setIsColTotalsChecked?: React.Dispatch<React.SetStateAction<boolean>>;
	isRowTotalsChecked?: boolean;
	setIsRowTotalsChecked?: React.Dispatch<React.SetStateAction<boolean>>;
	dataframeId?: number;
	selectedDate?: TNewDateRange | undefined;
	exportRequest?: DataframeDataRetrievalRequest;
};

function EntityMiscButtons(props: Props) {
	const [isOpenShareModal, setIsOpenShareModal] = useState(false);
	const [isOpenAlertModal, setIsOpenAlertModal] = useState(false);
	const isGranted = useIsGranted();
	const { addToast } = useToast();
	const [exportColumns, setExportColumns] = useState<Columns>();
	const [exportData, setExportData] = useState<Datas>();
	const [exportFileName, setExportFileName] = useState<string>();
	const navigate = useNavigate();
	const pathname = useLocation().pathname;
	const isChart = props.entityType.toLowerCase() == DataBuilderTypes.chart;
	const isDataframe = props.entityType.toLowerCase() == DataBuilderTypes.dataframe;
	const isReport = props.entityType.toLowerCase() == DataBuilderTypes.report;
	const isTable = props.entityType.toLowerCase() == DataBuilderTypes.table;
	const isPresentation = props.entityType.toLowerCase() == DataBuilderTypes.presentation;
	const isDashboard = props.entityType.toLowerCase() == DataBuilderTypes.dashboard;
	const haseditDataframePerm = isGranted(Permission.EditDataframe);
	const haseditChartPerm = isGranted(Permission.EditChart);
	const haseditReportPerm = isGranted(Permission.EditReport);
	const haseditTablePerm = isGranted(Permission.EditTable);
	const canAccessAlerts = isGranted(Permission.CreateAlert) || isGranted(Permission.EditAlert);
	const haseditPresentation = isGranted(Permission.EditPresentation);
	const editPresentationChecks = isPresentation && haseditPresentation && props.canEdit;
	const editDataframeChecks = isDataframe && haseditDataframePerm && props.canEdit;
	const editChartChecks = isChart && haseditChartPerm && props.canEdit;
	const editReportChecks = isReport && haseditReportPerm && props.canEdit;
	const editTableChecks = isTable && haseditTablePerm && props.canEdit;
	const editDashboardChecks = isDashboard && props.canEdit;
	const { measures, zoneSettings, unitTypes, user } = useApplication();
	const [filteredKeyMeasures, setFilteredKeyMeasures] = useState<TKeyMeasure[]>([]);
	const isFolderPath = pathname.includes('/folder');
	const entityId = pathname.split('/')[pathname.split('/').length - 1];
	const isEntity = !isNaN(+entityId);
	const [isExportOpen, setIsExportOpen] = useState(false);
	const [isrptTotalsOpen, setIsrptTotalsOpen] = useState(false);
	const [isInsightsDisabled, setIsInsightsDisabled] = useState(false);
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const [csvExportData, setCsvExportData] = useState<Datas>();

	useMount(() => {
		const disableZoneSetting = zoneSettings.find(
			(x) => x.zone_setting_enum_id == ZoneSettingEnum.AI_disabled
		);

		if (disableZoneSetting && disableZoneSetting.enabled) {
			setIsInsightsDisabled(true);
		}
	});

	const shiftDecimalRight = (value: string, decimals = 1) => {
		const num = Number(value);
		if (Number.isNaN(num)) return value;

		return (num * 100).toFixed(decimals);
	};

	useEffect(() => {
		const droppedFactsKeyValue = props.droppedFacts?.map((x) => ({
			id: x.data?.id,
			value: x.data?.title,
		}));

		const result = measures
			.map((keymeasure) => {
				// Filter the KeyMeasureFacts to only include those that match droppedFactsKeyValue
				const filteredFacts = keymeasure.keyMeasureFacts.filter((fact) =>
					droppedFactsKeyValue?.some((droppedFact) => droppedFact.id === fact.id)
				);

				filteredFacts.map((fact) => {
					const matchingFact = droppedFactsKeyValue?.find((x) => x.id == fact.id);

					if (matchingFact && matchingFact.value) {
						fact.name = matchingFact.value;
					}
				});

				// Return the keymeasure only if there are matching facts
				return filteredFacts.length > 0
					? { ...keymeasure, keyMeasureFacts: filteredFacts }
					: null;
			})
			.filter((keymeasure) => keymeasure !== null) as TKeyMeasure[]; // Remove any keymeasures without matching facts

		setFilteredKeyMeasures(result);
	}, [props.droppedFacts]);

	useEffect(() => {
		if (props.exportData && props.exportData.json && props.exportData.csvData) {
			setExportColumns(
				props.exportData.json.columns[props.exportData.json.columns.length - 1].map(
					(col) => {
						return {
							id: col.replace(/\s/g, ''),
							displayName: `"${col}"`,
						};
					}
				)
			);

			setExportData(
				props.exportData.csvData.map((data) => {
					return rebuildCsvStringArray(data, false);
				})
			);

			setExportFileName(`${props.entityName}_${dateToYYYYMMDDHHMMSS(new Date())}`);
		} else {
			setExportColumns(undefined);
			setExportData(undefined);
			setExportFileName(undefined);
		}
	}, [props.exportData]);

	const closeShareModal = () => {
		setIsOpenShareModal(false);
	};

	const closeAlertModal = () => {
		setIsOpenAlertModal(false);
	};

	const openShareModal = () => {
		setIsOpenShareModal(true);
	};

	const openAlertModal = () => {
		setIsOpenAlertModal(true);
	};

	const checkPermissionAndNavEdit = () => {
		if (editDataframeChecks || editReportChecks || editTableChecks || editPresentationChecks) {
			const editUrl = pathname.replace('/view/', '/edit/');
			navigate(editUrl);
		}
		if (editChartChecks || editDashboardChecks) {
			const isDashboardChart = pathname.includes('/dashboards/');

			if (isDashboardChart) {
				const editUrl = pathname.replace('/view', '');
				navigate(editUrl);
			} else {
				const editUrl = pathname.replace('/view/', '/build/');
				navigate(editUrl);
			}
		}
	};

	const handleExportXLSX = () => {
		if (!exportColumns) {
			addToast('Please preview data before exporting it.', AlertVariant.danger);
			return;
		}
		setIsExporting(true);

		const request = buildExportRequest(
			props.exportRequest,
			props.dataframeId,
			props.selectedDate
		);

		Dataframe.Retrieve(request)
			.then((response: MultipartResponse<DataframeDataRetrievalResponse>): void => {
				const newExportData = response.csvData?.map((data) => {
					return rebuildCsvStringArray(data, false);
				}) as Datas;

				// Helper to get header name safely
				const getHeader = (col: string | IColumn) =>
					typeof col === 'string' ? col : col.displayName ?? col.id;

				// Build headers from exportColumns
				const headers = exportColumns.map((col) => getHeader(col).replace(/"/g, ''));

				// response.json.columns carries every header row the query produced - for a
				// pivoted table (e.g. one column per Date Range period) that's a period-label
				// row above the measure-name row. exportColumns only keeps the last of those
				// rows, so fall back to `headers` alone when the fuller metadata isn't there.
				const headerRows: string[][] = response.json?.columns?.map((row) =>
					row.map((cell) => cell.replace(/"/g, ''))
				) ?? [headers];

				// Convert exportData (array of arrays or objects) into rows of values, keyed
				// positionally rather than by header name - pivoted columns (e.g. one period
				// per column) can share the same display name, which would collide if used as
				// an object key (see handleExportCSV's use of column.id for the same reason).
				const dataRows = newExportData.map((row) => {
					return exportColumns.map((col, index) => {
						const header = getHeader(col).replace(/"/g, '');
						let value = Array.isArray(row) ? row[index] : row[(col as IColumn).id];

						if (value) {
							const fact =
								props.droppedFacts &&
								props.droppedFacts.find((x) => x.data?.title == header);

							if (fact) {
								const factUnitType = unitTypes.find(
									(x) => x.id == fact.data?.unitType
								);

								if (factUnitType && factUnitType.code == 'PCT') {
									value = shiftDecimalRight(
										value.toString(),
										fact.data?.numDecimals
									);
								}
							}
						}

						// Ensure value is a string before using .replace()
						return value !== null && value !== undefined
							? String(value).replace(/"/g, '')
							: '';
					});
				});

				// Generate worksheet from header rows + data rows, positionally
				const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows]);

				// Create workbook and append the sheet
				const workbook = XLSX.utils.book_new();
				XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

				// Write file with timestamp
				const fileName = `${exportFileName ?? props.entityName}_${dateToYYYYMMDDHHMMSS(
					new Date()
				)}.xlsx`;

				setIsExporting(false);
				XLSX.writeFile(workbook, fileName);
			})
			.catch((): void => {
				addToast('An Error occured during Export.', AlertVariant.danger);
			});
	};

	const handleExportCSV = () => {
		if (!exportColumns) {
			addToast('Please preview data before exporting it.', AlertVariant.danger);
			return;
		}
		setIsExporting(true);

		const request = buildExportRequest(
			props.exportRequest,
			props.dataframeId,
			props.selectedDate
		);

		Dataframe.Retrieve(request)
			.then((response: MultipartResponse<DataframeDataRetrievalResponse>): void => {
				const newExportData = response.csvData?.map((data) => {
					return rebuildCsvStringArray(data, false);
				}) as Datas;

				// Helper to get header name safely
				const getHeader = (col: string | IColumn) =>
					typeof col === 'string' ? col : col.displayName ?? col.id;

				// response.json.columns carries every header row the query produced - for a
				// pivoted table (e.g. one column per Date Range period) that's a period-label
				// row above the measure-name row. exportColumns only keeps the last of those
				// rows, so fall back to a single row built from exportColumns when the fuller
				// metadata isn't there. CsvDownloader is told (via noHeader) to skip generating
				// its own header row, since these rows are written directly into the data.
				const headerRows: string[][] = response.json?.columns?.length
					? response.json.columns.map((row) => row.map((cell) => cell.replace(/"/g, '')))
					: [exportColumns.map((col) => getHeader(col).replace(/"/g, ''))];

				// Rows must stay positional arrays, matching headerRows above - CsvDownloader's
				// chunk processor infers array-vs-object shape from a chunk's first row only,
				// so mixing array header rows with object-keyed data rows breaks it (data rows
				// get treated as arrays and `.map` is called on a plain object).
				const dataRows: string[][] = newExportData.map((row) => {
					return exportColumns.map((col, index) => {
						const columnId = (col as IColumn).id;
						let value = Array.isArray(row) ? row[index] : row[columnId];

						if (value !== null && value !== undefined) {
							const header = getHeader(col).replace(/"/g, '');
							const fact = props.droppedFacts?.find((x) => x.data?.title === header);

							if (fact) {
								const factUnitType = unitTypes.find(
									(x) => x.id === fact.data?.unitType
								);

								if (factUnitType?.code === 'PCT') {
									value = shiftDecimalRight(
										value.toString(),
										fact.data?.numDecimals ?? 0
									);
								}
							}
						}

						return value !== null && value !== undefined
							? String(value).replace(/"/g, '')
							: '';
					});
				});

				setCsvExportData([...headerRows, ...dataRows]);
			})
			.catch((): void => {
				addToast('An Error occured during Export.', AlertVariant.danger);
			});

		// CSVDownloader handles this automatically via click
	};

	useEffect(() => {
		if (csvExportData && csvExportData?.length > 0) {
			const element = document.getElementsByClassName('my-csv-downloader')[0];
			if (element) {
				(element as HTMLElement).click();
				setIsExporting(false);
			}
		}
	}, [csvExportData]);

	const toggleFavorite = () => {
		if (props.favorite) {
			Favorites.ToggleFavorite(props.favorite)
				.then(() => {
					if (props.setFavorite) {
						props.setFavorite();
					}
				})
				.catch(() => {
					addToast('An error occured while saving a Favorite', AlertVariant.danger);
				});
		}
	};

	const navReportSchedule = () => {
		localStorage.removeItem('prevReportPage');
		navigate(`/report/schedules?reportId=${entityId}`);
	};

	//TODO: Add back once comment feature is completed
	// const commentsItem = (
	// 	<FlexItem>
	// 		<Button
	// 			variant="plain"
	// 			aria-label="Note Entity"
	// 			data-testid="note-entity"
	// 		>
	// 			<FontAwesomeIcon
	// 				icon={faMessage}
	// 				className="note-icon"
	// 				size="xl"
	// 				title="Comments"
	// 			/>
	// 		</Button>
	// 	</FlexItem>
	// );

	const exportItems = [
		<DropdownItem
			key="csv"
			component="button"
			isDisabled={!exportColumns}
		>
			{csvExportData?.length ? (
				// CSV data ready — show downloader inside dropdown
				<CsvDownloader
					className="my-csv-downloader"
					columns={exportColumns ?? []}
					datas={csvExportData ?? []}
					filename={exportFileName ?? 'export'}
					extension=".csv"
					separator=","
					noHeader
				>
					<span style={{ cursor: 'pointer' }}>Download as CSV</span>
				</CsvDownloader>
			) : (
				// CSV data not ready — show button to fetch data
				<button
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						handleExportCSV();
					}}
					style={{ all: 'unset', cursor: 'pointer' }}
				>
					Download as CSV
				</button>
			)}
		</DropdownItem>,
		<DropdownItem
			key="xlsx"
			component="button"
			onClick={handleExportXLSX}
			isDisabled={!exportColumns}
		>
			Download as XLSX
		</DropdownItem>,
	];

	const reportTotalItems = [
		<DropdownItem
			key="total1"
			component="button"
			onClick={() => {
				props.setIsColTotalsChecked &&
					props.setIsColTotalsChecked(!props.isColTotalsChecked);
			}}
		>
			<Checkbox
				id=""
				checked={props.isColTotalsChecked}
			></Checkbox>
			Column Summary
		</DropdownItem>,
		!isChart ? (
			<DropdownItem
				key="total2"
				component="button"
				onClick={() => {
					props.setIsRowTotalsChecked &&
						props.setIsRowTotalsChecked(!props.isRowTotalsChecked);
				}}
			>
				<Checkbox
					checked={props.isRowTotalsChecked}
					id=""
				></Checkbox>
				Row Summary
			</DropdownItem>
		) : null,
	].filter(Boolean);

	const exportButtonTemplate = () => {
		let ariaLabel = 'Download Entiy';
		let title = 'Download';
		if (isPresentation) {
			ariaLabel = 'Export to PPTX';
			title = 'Export to PPTX';
		}
		const template = (
			<Button
				variant="control"
				aria-label={ariaLabel}
				onClick={(e) => {
					if (!isPresentation) {
						if (exportData) {
							if (props.limitReached) {
								addToast(
									'More than 500 rows are present in the dataset. Apply filters to narrow your selection and reduce the number of rows.',
									AlertVariant.danger
								);
							}
						} else if (!exportData || !exportColumns) {
							addToast(
								'Please preview data before exporting it.',
								AlertVariant.danger
							);
						} else {
							e.preventDefault();
						}
					} else {
						props.exportPresentation && props.exportPresentation();
						e.preventDefault();
					}
				}}
			>
				<FontAwesomeIcon
					icon={faDownToBracket}
					className="download-icon"
					size="xl"
					title={title}
				/>
			</Button>
		);

		if (exportData && exportColumns && !props.limitReached && !isPresentation) {
			return (
				<CsvDownloader
					columns={exportColumns}
					datas={exportData}
					filename={exportFileName ?? ''}
					extension=".csv"
					separator=","
				>
					{template}
				</CsvDownloader>
			);
		}

		return template;
	};

	return (
		<div>
			<Flex
				className="lbl-entity-actions"
				flex={{ default: 'flex_1' }}
				style={isFolderPath ? { bottom: '0' } : {}}
			>
				<Flex spaceItems={{ default: 'spaceItemsNone' }}>
					<FlexItem>
						{isDashboard && props.isView && !isInsightsDisabled && (
							<Button
								variant="plain"
								aria-label="Insights"
								data-testid="dashboard-insights"
								onClick={() => {
									const value = props.isInsightsOpen ?? false;
									// Gate opening insights/chat by AI permissions
									const permissionsSet = new Set(user.permissions || []);
									const aiPermKeysPresent = (user.permissions || []).some(
										(p) => typeof p === 'string' && p.startsWith('canUseAI')
									);
									const hasAIChatPerm = aiPermKeysPresent
										? permissionsSet.has(Permission.CanUseAIChat)
										: true;
									if (hasAIChatPerm)
										props.openInsights && props.openInsights(value);
								}}
							>
								<FontAwesomeIcon
									icon={!props.isInsightsOpen ? faSparkles : faSparkleSolid}
									size="xl"
									title="Insights"
									color={props.isInsightsOpen ? '#0073c3' : 'unset'}
								/>
							</Button>
						)}
					</FlexItem>
					{(editDataframeChecks ||
						editChartChecks ||
						editReportChecks ||
						editTableChecks ||
						editPresentationChecks ||
						editDashboardChecks) &&
						props.isView && (
							<FlexItem>
								<Button
									variant="plain"
									aria-label="Edit"
									data-testid="edit"
									onClick={() => {
										checkPermissionAndNavEdit();
									}}
								>
									<FontAwesomeIcon
										icon={faPencil}
										size="xl"
										title="Edit"
									/>
								</Button>
							</FlexItem>
						)}
					{isChart && canAccessAlerts && (
						<FlexItem>
							<Button
								variant="plain"
								aria-label="Set Alert"
								data-testid="set-alert"
								onClick={() => {
									openAlertModal();
								}}
							>
								<FontAwesomeIcon
									icon={faSensorAlert}
									size="xl"
									title="Set Alert"
								/>
							</Button>
						</FlexItem>
					)}
					{props.favorite && (
						<FlexItem>
							<Button
								variant="plain"
								aria-label="Favorite Entity"
								data-testid="favorite-entity"
								onClick={() => {
									toggleFavorite();
								}}
							>
								<FontAwesomeIcon
									icon={
										props.favorite.name && props.favorite.created_at
											? faStarSolid
											: faStar
									}
									className="favorite-icon"
									size="xl"
									title={
										props.favorite.name && props.favorite.created_at
											? 'Remove Favorite'
											: 'Save as Favorite'
									}
								/>
							</Button>
						</FlexItem>
					)}
					{isReport && (
						<FlexItem>
							<Button
								variant="plain"
								aria-label="Set Report Schedule"
								data-testid="report-schedule"
								onClick={() => {
									navReportSchedule();
								}}
							>
								<FontAwesomeIcon
									icon={faCalendar}
									className="schedule-icon"
									size="xl"
									title={'Set Report Schedule'}
								/>
							</Button>
						</FlexItem>
					)}
					{(isEntity || isFolderPath) &&
						props.canShare &&
						isGranted(Permission.viewSharedEntity) && (
							<FlexItem>
								<Button
									variant="plain"
									aria-label="Share Entity"
									data-testid="share-entity"
									onClick={() => {
										openShareModal();
									}}
								>
									<FontAwesomeIcon
										icon={faShareNodes}
										className="share-icon"
										size="xl"
										title="Share"
									/>
								</Button>
							</FlexItem>
						)}
					{isPresentation && (
						<FlexItem>
							<Button
								variant="plain"
								aria-label="Present"
								data-testid="present-entity"
								onClick={() => {
									props.present && props.present();
								}}
							>
								<FontAwesomeIcon
									icon={faPersonChalkboard}
									className="present-icon"
									size="xl"
									title="Present"
								/>
							</Button>
						</FlexItem>
					)}
					{isPresentation && <FlexItem>{exportButtonTemplate()}</FlexItem>}
					{(isChart || isTable || isDataframe || isReport) && (
						<FlexItem>
							<Button
								variant="control"
								style={{ top: '5px' }}
								className="btn-no-border"
							>
								<Dropdown
									toggle={
										<DropdownToggle
											onToggle={() => setIsExportOpen((prev) => !prev)}
											aria-label="Download"
											className="no-color-override"
											icon={
												<FontAwesomeIcon
													icon={faDownToBracket}
													className="download-icon"
													size="xl"
													title="Download"
												/>
											}
											isPlain
										/>
									}
									isOpen={isExportOpen}
									dropdownItems={exportItems}
									onSelect={() => setIsExportOpen(false)}
									position="right"
								/>
							</Button>
						</FlexItem>
					)}
					{(isReport || isTable || isChart) && (
						<FlexItem>
							<Button
								variant="control"
								style={{ right: '5px' }}
								className="btn-no-border"
							>
								<Dropdown
									toggle={
										<DropdownToggle
											onToggle={() => setIsrptTotalsOpen((prev) => !prev)}
											aria-label="Download"
											className="no-color-override"
											icon={<b style={{ fontSize: '1.1rem' }}>Σ</b>}
											isPlain
										/>
									}
									isOpen={isrptTotalsOpen}
									dropdownItems={reportTotalItems}
									position="right"
								/>
							</Button>
						</FlexItem>
					)}
				</Flex>
			</Flex>

			{isGranted(Permission.viewSharedEntity) && (
				<ShareEntityModal
					entityType={props.entityType}
					entityName={props.entityName}
					isOpen={isOpenShareModal}
					onClose={closeShareModal}
				/>
			)}

			<AlertDefinitionModal
				isOpen={isOpenAlertModal}
				onClose={closeAlertModal}
				droppedFacts={props.droppedFacts ?? []}
				filteredKeyMeasures={filteredKeyMeasures}
			/>

			{isExporting && <Loader isFullScreen />}
		</div>
	);
}

export default EntityMiscButtons;
